import { Fragment, useEffect, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/solid";
import { ISortInput } from "types/api/product";
import cn from "classnames";
import { useTranslations } from "next-intl";

interface SimpleListBoxProps {
  items: ISortInput[];
  action: (data: ISortInput) => void;
  defaultSort?: { key: string; value: 1 | -1 };
}

const SimpleListBox: React.FC<SimpleListBoxProps> = ({
  items,
  action,
  defaultSort,
}) => {
  const t = useTranslations("");

  const [itemSelected, setSelected] = useState(items[0]);

  useEffect(() => {
    const _sort = [...items];

    if (defaultSort) {
      const _defaultSort = _sort.find((s) => {
        if (s.key === defaultSort.key && s.value === defaultSort.value) {
          return s;
        }
      });
      setSelected(_defaultSort || _sort[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSort]);

  const handelChangeSortItem = (data: ISortInput) => {
    setSelected({ ...data });
    action({ ...data });
  };

  return (
    <div className="w-full z-30">
      <Listbox value={itemSelected} onChange={handelChangeSortItem}>
        <div className="relative mt-1">
          <Listbox.Button className="relative flex w-full md:py-2 py-0 md:px-3 px-0 bg-white  cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 text-sm text-[#777]">
            <p className="block truncate text-[#008AF6] font-normal">
              <span className="text-[#999] px-1">{`${t("client_filters_sortproducts")}: `}</span>{" "}
              <span className="">{itemSelected?.title}</span>
            </p>
            <span className=" flex items-center pr-2 pointer-events-none">
              <ChevronDownIcon
                className="w-5 h-5 text-[#008AF6]"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute w-max right-0 py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {items.map((item, itemIdx) => (
                <Listbox.Option
                  key={item.key}
                  className={({ active }) =>
                    `cursor-default select-none relative py-2 pl-10 pr-4 ${active ? "text-white bg-brandBlue" : "text-[#333]"
                    }`
                  }
                  value={item}
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={`block truncate text-xs ${selected ? "font-medium " : "font-normal"
                          }`}
                      >
                        {item.title}
                      </span>
                      {selected ? (
                        <span
                          className={cn(
                            "absolute inset-y-0 left-0 flex items-center pl-3",
                            active === true ? "text-white" : "text-[#333]"
                          )}
                        >
                          <CheckIcon className="w-5 h-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};

export default SimpleListBox;
